import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  LayoutGrid, Sparkles, Bell, MessageCircle, X, Minus,
  BarChart3, Users, Megaphone, Package, Shield, FileText,
  Globe, UserCog, Crown, Settings, CircleDot, Inbox, Layers,
  ArrowUpRight, Check, User, ChevronUp, ChevronDown,
  Paperclip, Image, Mic, Send, SpellCheck, Languages,
  File, Video, Camera, Maximize2, Search, Tag, Bookmark,
  CreditCard, Ticket, Zap
} from 'lucide-react';
import { useWorkspace, WORKSPACE_PAGES } from '../contexts/WorkspaceContext';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const iconMap = {
  'grid': LayoutGrid, 'circle-dot': CircleDot, 'users': Users,
  'message-circle': Inbox, 'megaphone': Megaphone, 'package': Package,
  'bar-chart': BarChart3, 'shield': Shield, 'file-text': FileText,
  'globe': Globe, 'user-cog': UserCog, 'crown': Crown, 'settings': Settings,
  'user': User, 'credit-card': CreditCard, 'ticket': Ticket, 'zap': Zap,
  'sparkles': Sparkles, 'inbox': Inbox, 'link': Globe, 'bell': Bell,
};
const categoryLabels = {
  gestion: 'Gestion', comunicacion: 'Comunicacion', marketing: 'Marketing',
  analisis: 'Analisis', finanzas: 'Finanzas', admin: 'Administracion',
};
const categoryColors = {
  gestion: 'bg-brand-deep', comunicacion: 'bg-brand-green',
  marketing: 'bg-brand-copper', analisis: 'bg-brand-green',
  finanzas: 'bg-brand-copper', admin: 'bg-brand-deep',
};

const panelClass = "glass-bubble overflow-hidden";
const panelMobile = "sm:rounded-3xl rounded-3xl fixed inset-x-2 bottom-[44px] sm:inset-auto sm:bottom-20 max-h-[75vh] sm:max-h-[65vh]";

// ============== SWIPE DOWN TO CLOSE (mobile, like WhatsApp) ==============
const SwipeToClose = ({ onClose, children }) => {
  const start = React.useRef(null);
  const onTouchStart = (e) => {
    const scroller = e.target.closest('.overflow-y-auto');
    start.current = { y: e.touches[0].clientY, t: Date.now(), top: scroller ? scroller.scrollTop : 0 };
  };
  const onTouchEnd = (e) => {
    if (!start.current) return;
    const dy = e.changedTouches[0].clientY - start.current.y;
    const dt = Date.now() - start.current.t;
    if (start.current.top <= 0 && (dy > 90 || (dy > 45 && dt < 300))) onClose();
    start.current = null;
  };
  return <div className="contents" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="panel-swipe-area">{children}</div>;
};
const GrabHandle = () => <div className="sm:hidden mx-auto mt-2 mb-0.5 h-1 w-10 rounded-full bg-foreground/25" data-testid="panel-grab-handle" />;

// ============== PANEL: TABS ==============
const TabsPanel = React.memo(() => {
  const { pinnedTabs, restoreTab, closeTab, activeTabId } = useWorkspace();
  const { t } = useTranslation();
  return (
    <motion.div data-testid="panel-tabs"
      initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8, y: 20 }}
      transition={{ type: 'spring', damping: 22, stiffness: 350 }}
      className={cn("fixed z-[60] w-auto sm:w-72", panelClass, panelMobile, "sm:left-3")}>
      <GrabHandle />
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Layers className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
        <span className="text-sm font-semibold">{t('nav.tabs')}</span>
        <Badge variant="outline" className="text-[10px] ml-auto">{pinnedTabs.length}</Badge>
      </div>
      <div className="p-3 space-y-1.5 max-h-[70vh] sm:max-h-[55vh] overflow-y-auto">
        {pinnedTabs.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            <Layers className="w-7 h-7 mx-auto mb-2 opacity-40" />
            <p className="text-xs">{t('pill.noTabs')}</p>
          </div>
        ) : pinnedTabs.map(tab => {
          const Icon = iconMap[tab.icon] || LayoutGrid;
          const isActive = tab.id === activeTabId && !tab.minimized;
          return (
            <div key={tab.id} className="flex items-center gap-2">
              <button data-testid={`pinned-tab-${tab.id}`} onClick={() => restoreTab(tab.id)}
                className={cn("flex-1 flex items-center gap-3 p-2.5 rounded-xl transition-all text-left",
                  isActive ? "bg-violet-500/15 border border-violet-500/30" : "bg-white/5 hover:bg-white/10 border border-white/10")}>
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                </div>
                <span className="flex-1 text-sm font-medium truncate">{tab.label}</span>
                {isActive && <Check className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" />}
              </button>
              <button onClick={() => closeTab(tab.id)} className="p-1.5 rounded-lg hover:bg-destructive/20 transition-all opacity-60 hover:opacity-100">
                <X className="w-3 h-3 text-destructive" />
              </button>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
});

// ============== PANEL: NOAH ==============
const NoahPanel = React.memo(() => {
  const { chatWithNoah } = useApp();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([{ role: 'assistant', content: t('noah.greeting') }]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    const msg = input;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setTyping(true);
    try {
      const res = await chatWithNoah(msg);
      setMessages(prev => [...prev, { role: 'assistant', content: res.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, hubo un error. Intenta de nuevo.' }]);
    } finally { setTyping(false); }
  };

  return (
    <motion.div data-testid="panel-noah"
      initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8, y: 20 }}
      transition={{ type: 'spring', damping: 22, stiffness: 350 }}
      className={cn("fixed z-[60] w-auto sm:w-80", panelClass, panelMobile, "sm:left-16")}>
      <GrabHandle />
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
          <Sparkles className="w-3 h-3 text-white" />
        </div>
        <span className="text-sm font-semibold">Noah</span>
        <span className="text-[10px] text-emerald-700 dark:text-emerald-400 ml-auto">{t('noah.online')}</span>
      </div>
      <div className="flex flex-col h-[70vh] sm:h-[55vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
          {messages.map((msg, i) => (
            <div key={`noah-${i}`} className={cn("flex gap-2", msg.role === 'user' ? "flex-row-reverse" : "")}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className={cn("max-w-[85%] rounded-2xl px-3 py-2 text-sm",
                msg.role === 'user' ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"
              )}>{msg.content}</div>
            </div>
          ))}
          {typing && (
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-muted rounded-2xl rounded-bl-sm px-3 py-2">
                <div className="flex gap-1">
                  {[0, 150, 300].map(d => <span key={d} className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-white/10">
          <div className="flex gap-2">
            <input data-testid="noah-pill-input" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()} placeholder={t('noah.talkToNoah')}
              className="flex-1 bg-muted border-none rounded-xl px-3 py-2 text-[16px] sm:text-sm outline-none focus:ring-1 focus:ring-violet-500" />
            <button data-testid="noah-pill-send" onClick={send} disabled={!input.trim() || typing}
              className="p-2 rounded-xl bg-primary text-primary-foreground disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

// ============== PANEL: NOTIFICATIONS (clickable, routes to context) ==============
const NotificationsPanel = React.memo(() => {
  const { notifications, markNotificationRead } = useApp();
  const { openWorkspace, closeAllPanels } = useWorkspace();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const unread = notifications.filter(n => !n.read);

  const handleNotifClick = (n) => {
    if (n.id) markNotificationRead(n.id);
    const type = n.type || '';
    if (type === 'new_message' || type.includes('chat')) openWorkspace('pipeline');
    else if (type === 'sale_complete' || type.includes('payment') || type.includes('escrow')) openWorkspace('escrow');
    else if (type === 'new_customer') openWorkspace('customers');
    else if (type.includes('analytics') || type.includes('report')) openWorkspace('analytics');
    else if (type.includes('invoice')) openWorkspace('invoices');
    else openWorkspace('dashboard');
  };

  return (
    <motion.div data-testid="panel-notifications"
      initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8, y: 20 }}
      transition={{ type: 'spring', damping: 22, stiffness: 350 }}
      className={cn("fixed z-[60] w-auto sm:w-80", panelClass, panelMobile, "sm:right-16")}>
      <GrabHandle />
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Bell className="w-4 h-4 text-amber-700 dark:text-amber-400" />
        <span className="text-sm font-semibold">{t('nav.notifications')}</span>
        {unread.length > 0 && <Badge className="ml-auto bg-red-600 text-white text-[10px]">{unread.length}</Badge>}
      </div>
      <div className="max-h-[62vh] sm:max-h-[55vh] overflow-y-auto p-3 space-y-2">
        {notifications.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            <Bell className="w-7 h-7 mx-auto mb-2 opacity-40" /><p className="text-xs">{t('pill.noNotifications')}</p>
          </div>
        ) : notifications.slice(0, 25).map((n) => (
          <button key={n.id || n.created_at} onClick={() => handleNotifClick(n)}
            data-testid={`pill-notif-${n.id}`}
            className={cn("w-full text-left p-3 rounded-xl transition-all cursor-pointer hover:scale-[1.01]",
              n.read ? "bg-white/5 hover:bg-white/10" : "bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/15")}>
            <p className="text-sm font-medium">{n.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
            <p className="text-[9px] text-muted-foreground/60 mt-1">Click para ver</p>
          </button>
        ))}
      </div>
      <div className="p-2 border-t border-white/10">
        <button data-testid="pill-notifs-see-all"
          onClick={() => { closeAllPanels(); navigate('/notifications'); }}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium bg-white/5 hover:bg-white/10 transition-colors">
          Todas las notificaciones <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
});

// ============== PANEL: MESSENGER-STYLE CHATS ==============
const ChatsPanel = React.memo(() => {
  const { chats, campaigns, sendMessage, getChatMessages } = useApp();
  const { t, i18n } = useTranslation();
  const { addChatBubble, openWorkspace } = useWorkspace();
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const chatScrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const chatsByCampaign = {};
  chats.forEach(chat => {
    const cid = chat.campaign_id || 'sin_campana';
    if (!chatsByCampaign[cid]) chatsByCampaign[cid] = [];
    chatsByCampaign[cid].push(chat);
  });

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages]);

  const openMiniChat = async (chat) => {
    setSelectedChat(chat);
    setChatMessages(chat.messages || []);
    if (chat.id) {
      try {
        const msgs = await getChatMessages(chat.id);
        if (msgs?.length) setChatMessages(msgs);
      } catch { /* use cached */ }
    }
  };

  const handleSendMsg = async () => {
    if (!msgInput.trim() || !selectedChat) return;
    setSending(true);
    try {
      const result = await sendMessage(selectedChat.id, msgInput, 'agent');
      setChatMessages(prev => [...prev, result.message]);
      if (result.ai_response) setChatMessages(prev => [...prev, result.ai_response]);
      setMsgInput('');
    } catch { /* handled */ }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChat) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Max 25MB'); return; }
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/files/upload?chat_id=${selectedChat.id}&customer_id=${selectedChat.customer_id}`, formData,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      const fileMsg = `[${res.data.category === 'image' ? 'Imagen' : 'Archivo'}] ${res.data.filename}\n${fileUrl}`;
      await sendMessage(selectedChat.id, fileMsg, 'agent');
      toast.success('Archivo enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSpellCheck = () => {
    if (!msgInput.trim()) return;
    const lang = i18n.language === 'es' ? 'espanol' : 'english';
    toast.info(`Verificando ortografia (${lang})...`);
    // Uses browser spellcheck by focusing the input
    const el = document.querySelector('[data-testid="chat-pill-input"]');
    if (el) { el.setAttribute('spellcheck', 'true'); el.setAttribute('lang', i18n.language); el.focus(); }
  };

  const handleTranslate = async () => {
    if (!msgInput.trim()) return;
    const targetLang = i18n.language === 'es' ? 'English' : 'Espanol';
    toast.info(`Traduciendo a ${targetLang}...`);
    try {
      const res = await axios.post(`${API}/ai-assistant`, {
        message: `Translate this text to ${targetLang}. Only return the translation, nothing else: "${msgInput}"`,
        session_id: 'translator'
      });
      if (res.data?.response) setMsgInput(res.data.response);
    } catch {
      toast.error('Error al traducir');
    }
  };

  // Messenger-style mini chat
  if (selectedChat) {
    return (
      <motion.div data-testid="panel-chats-expanded"
        initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8, y: 20 }}
        transition={{ type: 'spring', damping: 22, stiffness: 350 }}
        className={cn("fixed z-[60] w-auto sm:w-96 sm:max-w-[400px]", panelClass, panelMobile, "sm:right-3")}>
      <GrabHandle />
        {/* Messenger header with min/max/close */}
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 bg-primary/20 rounded-t-3xl">
          <button onClick={() => setSelectedChat(null)} className="p-2 sm:p-1.5 rounded-full hover:bg-white/10 text-muted-foreground" title="Volver">
            <ArrowUpRight className="w-4 h-4 sm:w-3.5 sm:h-3.5 rotate-[225deg]" />
          </button>
          <div className="relative">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-primary">
              {selectedChat.customer?.name?.charAt(0) || '?'}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-card" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold truncate">{selectedChat.customer?.name || 'Cliente'}</p>
            <p className="text-[10px] text-emerald-700 dark:text-emerald-400">{t('noah.online')}</p>
          </div>
          <div className="flex items-center gap-0.5">
            <button onClick={() => { 
              addChatBubble(selectedChat);
              setSelectedChat(null); 
            }} className="p-1.5 rounded-full hover:bg-white/10 text-muted-foreground" title="Minimizar a burbuja">
              <Minus className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { 
              openWorkspace('pipeline');
              setSelectedChat(null); 
            }} className="p-1.5 rounded-full hover:bg-white/10 text-muted-foreground" title="Maximizar en Pipeline">
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setSelectedChat(null)} className="p-1.5 rounded-full hover:bg-red-500/20 text-red-400" title="Cerrar">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Messages - Messenger style */}
        <div ref={chatScrollRef} className="h-[55vh] sm:h-[45vh] overflow-y-auto p-3 space-y-1.5">
          {chatMessages.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">{t('pill.noMessages')}</p>}
          {chatMessages.map((msg, idx) => {
            const isMe = msg.sender === 'agent' || msg.sender === 'user';
            const isAi = msg.sender === 'ai';
            return (
              <div key={msg.id || `m-${idx}`} className={cn("flex gap-1.5", isMe ? "flex-row-reverse" : "")}>
                {!isMe && (
                  <div className={cn("w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-auto",
                    isAi ? "bg-primary" : "bg-brand-green")}>
                    {isAi ? <Sparkles className="w-3 h-3 text-white" /> :
                      <span className="text-[8px] font-bold text-white">{selectedChat.customer?.name?.charAt(0) || '?'}</span>}
                  </div>
                )}
                <div className={cn("max-w-[78%] px-3 py-2 text-xs leading-relaxed",
                  isMe ? "bg-[#0084ff] text-white rounded-[18px] rounded-br-[4px]"
                    : isAi ? "bg-violet-500/15 border border-violet-500/20 rounded-[18px] rounded-bl-[4px]"
                    : "bg-muted rounded-[18px] rounded-bl-[4px]"
                )}>
                  {isAi && <p className="text-[9px] text-brand-deep dark:text-brand-deep dark:text-brand-green font-semibold mb-0.5">Noah</p>}
                  <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                  {isAi && msg.voice_score && (
                    <div className="mt-1 flex items-center gap-1" data-testid={`voice-score-badge-${msg.id}`}>
                      <span className={cn(
                        "text-[9px] px-1.5 py-0.5 rounded-full font-semibold",
                        msg.voice_score.score >= 85 ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" :
                        msg.voice_score.score >= 70 ? "bg-amber-500/20 text-amber-700 dark:text-amber-400" :
                        msg.voice_score.score >= 50 ? "bg-orange-500/20 text-amber-700 dark:text-amber-400" :
                        "bg-red-500/20 text-red-400"
                      )} title={msg.voice_score.feedback}>
                        {msg.voice_score.score}% como tú
                      </span>
                    </div>
                  )}
                  {msg.timestamp && <p className={cn("text-[9px] mt-1 opacity-60", isMe ? "text-white/60" : "text-muted-foreground")}>
                    {new Date(msg.timestamp).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                  </p>}
                </div>
              </div>
            );
          })}
          {sending && (
            <div className="flex gap-1.5">
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center mt-auto">
                <Sparkles className="w-3 h-3 text-white animate-pulse" />
              </div>
              <div className="bg-violet-500/15 rounded-[18px] rounded-bl-[4px] px-3 py-2">
                <div className="flex gap-1">
                  {[0, 100, 200].map(d => <span key={d} className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tools bar */}
        <AnimatePresence>
          {showTools && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="border-t border-white/10 px-2 py-1.5 flex items-center gap-1 overflow-hidden">
              <button onClick={handleSpellCheck} className="p-1.5 rounded-lg hover:bg-violet-500/10 text-muted-foreground hover:text-brand-deep dark:text-brand-deep dark:text-brand-green transition-colors" title="Ortografia">
                <SpellCheck className="w-4 h-4" />
              </button>
              <button onClick={handleTranslate} className="p-1.5 rounded-lg hover:bg-brand-green/10 text-muted-foreground hover:text-brand-deep dark:text-brand-green transition-colors" title="Traducir">
                <Languages className="w-4 h-4" />
              </button>
              <div className="w-px h-5 bg-white/10 mx-0.5" />
              <button onClick={() => fileInputRef.current?.click()} className="p-1.5 rounded-lg hover:bg-brand-green/10 text-muted-foreground hover:text-brand-deep dark:text-brand-green transition-colors" title="Imagen">
                <Image className="w-4 h-4" />
              </button>
              <button onClick={() => { fileInputRef.current?.setAttribute('accept', 'video/*,audio/*'); fileInputRef.current?.click(); }} 
                className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-muted-foreground hover:text-emerald-700 dark:text-emerald-400 transition-colors" title="Media">
                <Video className="w-4 h-4" />
              </button>
              <button onClick={() => { fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv'); fileInputRef.current?.click(); }}
                className="p-1.5 rounded-lg hover:bg-amber-500/10 text-muted-foreground hover:text-amber-700 dark:text-amber-400 transition-colors" title="Documento">
                <File className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input - Messenger style */}
        <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" />
        <div className="p-2 border-t border-white/10 flex items-end gap-1.5">
          <button onClick={() => setShowTools(v => !v)}
            className={cn("p-2 rounded-full transition-colors flex-shrink-0",
              showTools ? "bg-violet-500/20 text-brand-deep dark:text-brand-deep dark:text-brand-green" : "hover:bg-white/10 text-muted-foreground")}>
            <Paperclip className="w-4 h-4" />
          </button>
          <div className="flex-1 relative">
            <input data-testid="chat-pill-input" value={msgInput} onChange={e => setMsgInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMsg(); }}}
              placeholder={t('inbox.typeMessage')} spellCheck="true" lang={i18n.language}
              className="w-full bg-muted/50 border border-white/10 rounded-full px-4 py-2.5 sm:py-2 text-[16px] sm:text-xs placeholder:text-muted-foreground outline-none focus:border-violet-500/40 pr-10" />
          </div>
          <button data-testid="chat-pill-send" onClick={handleSendMsg} disabled={!msgInput.trim() || sending}
            className="p-2 rounded-full bg-[#0084ff] text-white disabled:opacity-40 flex-shrink-0 hover:bg-[#0073e6] transition-colors">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  }

  // Chat list
  return (
    <motion.div data-testid="panel-chats"
      initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8, y: 20 }}
      transition={{ type: 'spring', damping: 22, stiffness: 350 }}
      className={cn("fixed z-[60] w-auto sm:w-80", panelClass, panelMobile, "sm:right-3")}>
      <GrabHandle />
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
        <span className="text-sm font-semibold">{t('nav.liveChats')}</span>
        <Badge variant="outline" className="text-[10px] ml-auto">{chats.length}</Badge>
      </div>
      <div className="max-h-[62vh] sm:max-h-[55vh] overflow-y-auto p-3 space-y-3">
        {Object.entries(chatsByCampaign).map(([campId, campChats]) => {
          const campaign = campaigns.find(c => c.id === campId);
          return (
            <div key={campId}>
              <div className="flex items-center gap-2 mb-1.5 px-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: campaign?.color || '#888' }} />
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                  {campaign?.name || t('pill.noCampaign')}
                </span>
                <Badge variant="outline" className="text-[9px] ml-auto">{campChats.length}</Badge>
              </div>
              <div className="space-y-1">
                {campChats.slice(0, 8).map(chat => (
                  <button key={chat.id} data-testid={`chat-pill-${chat.id}`} onClick={() => openMiniChat(chat)}
                    className="w-full flex items-center gap-2.5 p-2 rounded-xl text-left bg-white/5 hover:bg-white/10 border border-white/10 transition-all">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-primary flex-shrink-0">
                        {chat.customer?.name?.charAt(0) || '?'}
                      </div>
                      {chat.unread_count > 0 && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-card" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{chat.customer?.name || 'Cliente'}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{chat.last_message || t('pill.noMessages')}</p>
                    </div>
                    {chat.unread_count > 0 && (
                      <span className="w-5 h-5 rounded-full bg-[#0084ff] text-white text-[9px] flex items-center justify-center flex-shrink-0 font-bold">
                        {chat.unread_count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {chats.length === 0 && (
          <div className="text-center py-6 text-muted-foreground text-sm">
            <MessageCircle className="w-7 h-7 mx-auto mb-2 opacity-40" /><p className="text-xs">{t('pill.noChats')}</p>
          </div>
        )}
      </div>
      <div className="p-2 border-t border-white/10">
        <button data-testid="pill-chats-open-inbox" onClick={() => openWorkspace('inbox')}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium bg-white/5 hover:bg-white/10 transition-colors">
          Ver todo el Inbox <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
});

// ============== DEAL MENU ==============
const DealMenu = React.memo(() => {
  const { openWorkspace, openTabs } = useWorkspace();
  const { t } = useTranslation();
  const grouped = {};
  WORKSPACE_PAGES.forEach(p => { if (!grouped[p.category]) grouped[p.category] = []; grouped[p.category].push(p); });

  return (
    <div className="fixed z-[60] inset-x-0 bottom-[44px] sm:bottom-20 sm:inset-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-auto sm:max-w-lg w-full pointer-events-none">
      <motion.div data-testid="deal-menu"
        initial={{ opacity: 0, scale: 0.85, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.85, y: 20 }}
        transition={{ type: 'spring', damping: 22, stiffness: 350 }}
        className={cn("pointer-events-auto mx-2 sm:mx-0", panelClass, "rounded-3xl max-h-[75vh] sm:max-h-[70vh]")}>
      <GrabHandle />
      <div className="p-2.5 border-b border-white/10 text-center">
        <h3 className="text-sm font-bold text-primary">
          DEAL - {t('nav.workspaces')}
        </h3>
      </div>
      <div className="p-3 max-h-[72vh] sm:max-h-[58vh] overflow-y-auto space-y-4">
        {Object.entries(grouped).map(([cat, pages]) => (
          <div key={cat}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">
              {categoryLabels[cat] || cat}
            </p>
            <div className="grid grid-cols-3 min-[420px]:grid-cols-4 sm:grid-cols-5 gap-2">
              {pages.map(page => {
                const Icon = iconMap[page.icon] || LayoutGrid;
                const isOpen = openTabs.some(t => t.id === page.id);
                return (
                  <button key={page.id} data-testid={`deal-menu-${page.id}`} onClick={() => openWorkspace(page.id)} lang="es"
                    className={cn("flex flex-col items-center justify-start gap-1.5 p-2 rounded-xl transition-all active:scale-95 w-full min-h-[84px] hyphens-auto",
                      isOpen ? "bg-primary/15 border border-primary/40" : "bg-white/5 hover:bg-white/10 border border-white/10")}>
                    <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center shadow-md shrink-0", categoryColors[cat])}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[10px] font-medium text-center leading-tight whitespace-normal break-words w-full">{page.label}</span>
                    {isOpen && <div className="w-1 h-1 rounded-full bg-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      </motion.div>
    </div>
  );
});

// ============== WORKSPACE TAB BAR ==============
export const WorkspaceTabBar = React.memo(() => {
  const { visibleTabs, activeTabId, setActiveTabId, closeTab, minimizeTab } = useWorkspace();
  if (visibleTabs.length <= 1) return null;

  return (
    <div data-testid="workspace-tab-bar" className="flex items-center gap-1 px-2 sm:px-4 pt-2 pb-0 overflow-x-auto no-scrollbar safe-top">
      {visibleTabs.map(tab => {
        const Icon = iconMap[tab.icon] || LayoutGrid;
        const isActive = tab.id === activeTabId;
        return (
          <div key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTabId(tab.id)}
            className={cn("flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-t-lg cursor-pointer transition-colors group whitespace-nowrap border-b-2",
              isActive ? "bg-background border-violet-500 text-foreground" : "bg-muted/30 border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50")}>
            <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="text-[10px] sm:text-xs font-medium">{tab.label}</span>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={e => { e.stopPropagation(); minimizeTab(tab.id); }} className="p-0.5 rounded hover:bg-muted"><Minus className="w-3 h-3" /></button>
              <button onClick={e => { e.stopPropagation(); closeTab(tab.id); }} className="p-0.5 rounded hover:bg-destructive/20 hover:text-destructive"><X className="w-3 h-3" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
});

// ============== 5-PILL NAV BAR (draggable, minimizable, with chat bubbles) ==============
export const BubblePillNav = () => {
  const { activePanel, togglePanel, closeAllPanels, pinnedTabs, chatBubbles, removeChatBubble } = useWorkspace();
  const { notifications, chats } = useApp();
  const { t } = useTranslation();
  const [minimized, setMinimized] = useState(false);
  const unreadNotifs = notifications.filter(n => !n.read).length;
  const unreadChats = chats.filter(c => c.unread_count > 0).length;

  // Touch drag to minimize/expand
  const dragStartY = useRef(null);
  const handleTouchStart = useCallback((e) => {
    dragStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (dragStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - dragStartY.current;
    if (deltaY > 48) setMinimized(true);
    if (deltaY < -48) setMinimized(false);
    dragStartY.current = null;
  }, []);

  const pillBase = "relative flex items-center justify-center rounded-full transition-colors duration-150 active:scale-95 touch-manipulation select-none shrink-0";
  const pillSize = "w-7 h-7 sm:w-9 sm:h-9";
  const navShell = "fixed bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 z-[70] flex items-center rounded-full " +
    "bg-card/85 backdrop-blur-2xl border border-white/20 shadow-[0_8px_40px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)] " +
    "select-none max-w-[calc(100vw-0.75rem)] h-8 sm:h-12 px-0.5 sm:px-1 gap-0.5 sm:gap-1";

  if (minimized) {
    return (
      <nav data-testid="bubble-pill-nav-mini"
        onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
        className={cn(navShell, "justify-center")}>
        <button data-testid="pill-expand" onClick={() => setMinimized(false)}
          className="flex items-center gap-1.5 px-3 sm:px-4 h-7 sm:h-9 rounded-full bg-primary/25 hover:bg-primary/35 transition-colors active:scale-95 touch-manipulation">
          <ChevronUp className="w-3.5 h-3.5 text-brand-deep dark:text-brand-green" />
          <span className="text-[11px] font-bold tracking-wider text-brand-deep dark:text-brand-green">DEAL</span>
        </button>
        {(unreadNotifs > 0 || unreadChats > 0) && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse mr-1" />}
      </nav>
    );
  }

  return (
    <>
      <AnimatePresence>
        {activePanel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[55] bg-black/20 backdrop-blur-[2px]" onClick={closeAllPanels} />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {activePanel && (
          <SwipeToClose key={activePanel} onClose={closeAllPanels}>
            {activePanel === 'tabs' && <TabsPanel />}
            {activePanel === 'noah' && <NoahPanel />}
            {activePanel === 'deal' && <DealMenu />}
            {activePanel === 'notifications' && <NotificationsPanel />}
            {activePanel === 'chats' && <ChatsPanel />}
          </SwipeToClose>
        )}
      </AnimatePresence>

      <nav data-testid="bubble-pill-nav"
        onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
        className={cn(navShell, "justify-center")}>

        <button data-testid="pill-tabs" onClick={() => togglePanel('tabs')}
          className={cn(pillBase, pillSize,
            activePanel === 'tabs' ? "bg-primary text-primary-foreground shadow-lg"
              : "bg-white/10 hover:bg-white/15 text-foreground")}>
          <Layers className="w-4 h-4" />
          {pinnedTabs.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-brand-copper text-white text-[8px] flex items-center justify-center font-bold shadow">{pinnedTabs.length}</span>}
        </button>

        <button data-testid="pill-noah" onClick={() => togglePanel('noah')}
          className={cn(pillBase, pillSize,
            activePanel === 'noah' ? "bg-brand-copper text-white shadow-lg"
              : "bg-white/10 hover:bg-white/15 text-foreground")}>
          <Sparkles className="w-4 h-4" />
        </button>

        <button data-testid="pill-deal" onClick={() => togglePanel('deal')}
          className={cn("relative shrink-0 flex items-center gap-1 px-2.5 sm:px-3.5 h-7 sm:h-9 rounded-full transition-colors duration-150 active:scale-95 touch-manipulation",
            activePanel === 'deal' ? "bg-primary text-primary-foreground shadow-lg"
              : "bg-primary/15 hover:bg-primary/25 text-foreground border border-primary/30")}>
          <LayoutGrid className="w-3.5 h-3.5 shrink-0" />
          <span className="text-[10px] sm:text-xs font-bold tracking-wider">DEAL</span>
        </button>

        <button data-testid="pill-notifications" onClick={() => togglePanel('notifications')}
          className={cn(pillBase, pillSize,
            activePanel === 'notifications' ? "bg-brand-copper text-white shadow-lg"
              : "bg-white/10 hover:bg-white/15 text-foreground")}>
          <Bell className="w-4 h-4" />
          {unreadNotifs > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-3.5 h-3.5 px-0.5 rounded-full bg-red-600 text-white text-[8px] flex items-center justify-center font-bold shadow">{unreadNotifs > 9 ? '9+' : unreadNotifs}</span>}
        </button>

        <button data-testid="pill-chats" onClick={() => togglePanel('chats')}
          className={cn(pillBase, pillSize,
            activePanel === 'chats' ? "bg-primary text-primary-foreground shadow-lg"
              : "bg-white/10 hover:bg-white/15 text-foreground")}>
          <MessageCircle className="w-4 h-4" />
          {unreadChats > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-3.5 h-3.5 px-0.5 rounded-full bg-brand-green text-white text-[8px] flex items-center justify-center font-bold shadow">{unreadChats > 9 ? '9+' : unreadChats}</span>}
        </button>

        <button data-testid="pill-minimize" onClick={() => setMinimized(true)}
          className={cn(pillBase, "w-6 h-6 bg-white/5 hover:bg-white/10")} title={t('pill.minimize')}>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </nav>

      {/* Minimized chat bubbles - floating above pill */}
      {chatBubbles.length > 0 && (
        <div className="fixed bottom-[48px] sm:bottom-[72px] right-3 z-50 flex flex-col-reverse gap-2 safe-bottom">
          {chatBubbles.map(bubble => (
            <div key={bubble.id} className="relative group">
              <button onClick={() => togglePanel('chats')}
                className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold shadow-lg hover:scale-110 transition-all">
                {bubble.customer_name?.charAt(0) || '?'}
              </button>
              <button onClick={() => removeChatBubble(bubble.id)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
